/**
 * Redux middleware that intercepts fulfilled RTK Query mutations
 * and logs them as activity events to the backend + App Insights.
 *
 * Events are batched (10s window) to avoid per-mutation network calls.
 * Fire-and-forget: never blocks Redux dispatch pipeline.
 */
import type { Middleware } from "@reduxjs/toolkit";
import type { IRootReducerState } from "../IRootReducer";
import { getInstanceIdFromUrl } from "@/utils/instanceId";
import { appInsightsService } from "@/services/appInsights.service";
import {
  classifyEndpoint,
  classifyCategory,
  humanizeEndpoint,
  SKIPPED_ENDPOINTS,
} from "./activityClassifier";
import { PRODUCT_API_PREFIX } from "../../../shared/products/keys";

interface FulfilledMutationAction {
  type: string;
  meta: {
    arg: {
      endpointName: string;
      type: "mutation";
    };
    requestId: string;
    /** fetchBaseQuery's `{ request, response }`, carried onto the fulfilled action by RTK Query. */
    baseQueryMeta?: { request?: { url?: string } };
  };
  payload: unknown;
}

const API_ROOT = "/api/v1";
const PRODUCT_API_PATHS = Object.values(PRODUCT_API_PREFIX).map((prefix) => `${API_ROOT}${prefix}`);

/**
 * A call to a non-default product's API (`/api/v1/performance/...`). The server audits those with
 * a product tag that keeps them out of the hire feed; a row from here would carry no tag and land
 * in it (Task 11 M4). Matched on the request URL because this hire module may not import the
 * product's endpoint names (spec §2 import boundaries).
 */
function isProductApiRequest(action: FulfilledMutationAction): boolean {
  const url = action.meta.baseQueryMeta?.request?.url;
  if (typeof url !== "string") return false;
  let path: string;
  try {
    path = new URL(url, window.location.origin).pathname;
  } catch {
    return false;
  }
  return PRODUCT_API_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function isFulfilledMutation(action: unknown): action is FulfilledMutationAction {
  if (typeof action !== "object" || action === null) return false;
  const a = action as Record<string, unknown>;
  if (typeof a.type !== "string" || !a.type.endsWith("/fulfilled")) return false;
  const meta = a.meta as Record<string, unknown> | undefined;
  if (!meta) return false;
  const arg = meta.arg as Record<string, unknown> | undefined;
  if (!arg) return false;
  return arg.type === "mutation" && typeof arg.endpointName === "string";
}

// --- Batching: queue events and flush every 10 seconds ---
interface QueuedEvent {
  actionType: string;
  category: string;
  pagePath: string;
  pageTitle: string;
  sessionId?: string;
  details: { endpoint: string; description: string };
}

const eventQueue: QueuedEvent[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL = 10_000; // 10 seconds

function flushEvents() {
  if (eventQueue.length === 0) return;

  const events = eventQueue.splice(0);
  flushTimeout = null;

  const accessToken = lastAccessToken;
  const instanceId = getInstanceIdFromUrl();
  if (!accessToken || !instanceId) return;

  const sessionId = sessionStorage.getItem("activity_session_id") || undefined;

  fetch("/api/v1/activity-logs/batch", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      instanceId,
      events: events.map((e) => ({ ...e, sessionId })),
    }),
  }).catch(() => {
    // Silently fail — activity tracking must never break the app
  });
}

let lastAccessToken: string | null = null;

export const activityTrackingMiddleware: Middleware = (storeApi) => (next) => (action) => {
  const result = next(action);

  try {
    if (!isFulfilledMutation(action)) return result;

    const endpointName = action.meta.arg.endpointName;
    if (SKIPPED_ENDPOINTS.has(endpointName)) return result;
    if (isProductApiRequest(action)) return result;

    const state = storeApi.getState() as IRootReducerState;
    const accessToken = state.authReducer?.accessToken;
    const instanceId = getInstanceIdFromUrl();

    if (!accessToken || !instanceId) return result;

    lastAccessToken = accessToken;

    const actionType = classifyEndpoint(endpointName);
    const category = classifyCategory(actionType);
    const description = humanizeEndpoint(endpointName);

    // Queue event for batched sending
    eventQueue.push({
      actionType,
      category,
      pagePath: window.location.pathname + window.location.search,
      pageTitle: document.title,
      details: { endpoint: endpointName, description },
    });

    if (!flushTimeout) {
      flushTimeout = setTimeout(flushEvents, FLUSH_INTERVAL);
    }

    // Track to App Insights (lightweight, no network call)
    appInsightsService.trackEvent("MutationActivity", {
      endpointName,
      actionType,
      category,
      description,
      instanceId,
      pagePath: window.location.pathname,
    });
  } catch {
    // Never let tracking errors break the Redux dispatch pipeline
  }

  return result;
};
